import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { TEAMSPACE_QUERY_KEYS } from '@/entities/teamspace/lib/teamspaceQueryKeys';

import { USER_QUERY_KEYS } from '../lib/userQueryKeys';
import { useCurrentUser } from './useCurrentUser';

// Every settings query (any space): the plan's features come with them, not with the user.
const SETTINGS_QUERY_KEY = USER_QUERY_KEYS.userSettings().slice(0, 1);
const SPACE_CREDITS_QUERY_KEY = TEAMSPACE_QUERY_KEYS.spaceCredits('').slice(0, 1);

// After a plan change in the app (no page load): the user and, with them, the new plan's features and credits. Twice, because the
// subscription may reach the backend (Stripe webhook) after the first refresh.
export const useReliableUserRefresh = () => {
  const { refetch } = useCurrentUser();
  const queryClient = useQueryClient();

  const refreshUserAndSettings = useCallback(async () => {
    const result = await refetch();
    await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: SPACE_CREDITS_QUERY_KEY });
    return result;
  }, [queryClient, refetch]);

  const reliableRefresh = useCallback(async () => {
    // First delay: 5 seconds
    await new Promise((resolve) => setTimeout(resolve, 5000));

    // First refresh (awaited)
    const result = await refreshUserAndSettings();

    // Second refresh: 10 seconds later (fire and forget)
    setTimeout(() => {
      void refreshUserAndSettings();
    }, 10_000);

    return result;
  }, [refreshUserAndSettings]);

  return { reliableRefresh };
};
